import { isSuccess } from '@devexperts/remote-data-ts'
import { Middleware } from '@reduxjs/toolkit'

import { AppSliceState } from '@/store/appSlice.ts'
import { BookingAvailabilitySliceState } from '@/store/bookingAvailabilitySlice.ts'
import { CartSliceState } from '@/store/cartSlice/type.ts'
import { PeriodSliceState } from '@/store/insertPeriodSlice.ts'
import { MapSliceState } from '@/store/mapSlice.ts'
import { cartSliceInitialState } from './cartSlice/initialState'
import { RootState } from './configureStore'

export const SESSION_STORAGE_REDUX_KEY = 'reduxState'
const SESSION_LICENSE_NAME = 'license'
export const SESSION_STORAGE_CART_CREATION_TIMESTAMP_KEY =
  'cartCreationTimeStamp'

export interface SessionStorageData {
  app: AppSliceState
  bookingAvailability: BookingAvailabilitySliceState
  cart: CartSliceState
  insertPeriod: PeriodSliceState
  map: MapSliceState
}

export const sessionStorageMiddleware: Middleware =
  (storeAPI) => (next) => (action) => {
    const result = next(action)
    const state: RootState = storeAPI.getState()
    const remoteDataLicense = state.license.license

    const initialSessionData = {
      app: state.app,
      bookingAvailability: state.bookingAvailability,
      cart: cartSliceInitialState,
      insertPeriod: state.insertPeriod,
    }

    const updatedSessionData = {
      app: state.app,
      bookingAvailability: state.bookingAvailability,
      cart: state.cart,
      insertPeriod: state.insertPeriod,
      map: state.map,
    }

    const sessionDataMap: Record<string, Partial<SessionStorageData>> = {
      chooseProduct: initialSessionData,
      insertData: updatedSessionData,
      insertInsurance: updatedSessionData,
      insertMap: initialSessionData,
      insertPeriod: { app: state.app },
      insertSector: initialSessionData,
      pay: updatedSessionData,
      thankYou: updatedSessionData,
      tickets: initialSessionData,
    }

    let sessionData: Partial<SessionStorageData> = {
      app: state.app,
      bookingAvailability: state.bookingAvailability,
      cart: state.cart,
      insertPeriod: state.insertPeriod,
      map: state.map,
    }

    const matchedPathName = Object.keys(sessionDataMap).find((key) =>
      window.location.pathname.includes(key)
    )
    if (matchedPathName) {
      sessionData = sessionDataMap[matchedPathName]
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_REDUX_KEY)
    }

    sessionStorage.setItem(
      SESSION_STORAGE_REDUX_KEY,
      JSON.stringify(sessionData)
    )

    if (isSuccess(remoteDataLicense)) {
      sessionStorage.setItem(
        SESSION_LICENSE_NAME,
        remoteDataLicense.value.license.license
      )
    }

    return result
  }

export function getSessionStorageData(): SessionStorageData | null {
  const data = sessionStorage.getItem(SESSION_STORAGE_REDUX_KEY)
  if (data) {
    const parsedData: SessionStorageData = JSON.parse(data)
    return {
      ...parsedData,
      cart: {
        ...parsedData.cart,
        cartDetailResponse: cartSliceInitialState.cartDetailResponse,
      },
    }
  }
  return null
}

export function getSessionLicense() {
  return sessionStorage.getItem(SESSION_LICENSE_NAME)
}

export function resetSessionStorageData() {
  sessionStorage.clear()
}
